import { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataMyIngredientsLocal,
  fetchDataMyIngredients,
  prependMyIngredients,
  removeMyIngredients,
  updateMyIngredients,
  sortMyIngredients,
  requestNewIngradient,
  resetRequestNewIngradient,
} from "./../store/actions";
import {
  UpdateMyIngredientActionEnum,
} from "./../store/types";
import type { TIngredient, TOption } from "./../store/types";
import momentTz from "moment-timezone";
// import { useAppDispatch } from "core/hooks/useAppDispatch";
import { appLang, removeVietnameseTones } from "core/utils/helper";

export const useMyIngredient = () => {
  const dispatch = useDispatch()
  // const appDispatch = useAppDispatch()
  const user = useSelector((state: any) => state.Auth.userInfo);
  const myIngredients = useSelector(
    (state: any) => state.Ingredients.myIngredients
  );
  const {loading, data: ingredients, filter} = myIngredients;

  useEffect(() => {
    // if (user?.id) {
      dispatch(getDataMyIngredientsLocal({
        user_id: user?.id,
        lang: appLang(),
      }))
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, appLang])

  const setSort = (e: TOption) => {
    dispatch(sortMyIngredients(e))
  }

  const addToMyIngredients = useCallback((items: TIngredient[]) => {
    if (items.length) {
      dispatch(updateMyIngredients({
        action: UpdateMyIngredientActionEnum.ADD,
        ids: items.map((e: any) => e.id)
      }))
      dispatch(prependMyIngredients({
        user_id: user?.id,
        lang: appLang(),
        items: items.map((e: TIngredient) => ({
          ...e,
          created_at: momentTz.tz('Etc/UTC').format('YYYY-MM-DD HH:mm:ss')
        }))
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prependMyIngredients, appLang, user])

  const onRemoveMyIngredients = useCallback((item: TIngredient | TIngredient[]) => {
    dispatch(updateMyIngredients({
      action: UpdateMyIngredientActionEnum.REMOVE,
      ids: Array.isArray(item) ? item.map((e: TIngredient) => e.id) : [item.id]
    }))
    dispatch(removeMyIngredients({
      user_id: user?.id,
      lang: appLang(),
      ids: Array.isArray(item) ? item.map((e: TIngredient) => e.id) : [item.id]
    }))
  }, [dispatch, appLang])

  const reload = () => {
    dispatch(fetchDataMyIngredients())
  }

  const checkExist = (ingredient: TIngredient) => {
    if (!user?.id) {
      return false
    }
    return ingredients.map((item: TIngredient) => item.id).indexOf(ingredient.id) !== -1
  }

  return {
    loading,
    ingredients,
    addToMyIngredients,
    onRemoveMyIngredients,
    sort: filter?.sort,
    setSort,
    reload,
    checkExist,
  }
}

export const useRequestIngredient = (
  onRequestSuccess?: (data: TIngredient) => void
) => {
  const dispatch = useDispatch()
  const request = useSelector((state: any) => state.Ingredients.request);
  const { loading, data, error } = request;
  const myIngredients = useSelector(
    (state: any) => state.Ingredients.myIngredients
  );
  const { data: ingredients } = myIngredients;
  const { data: globalIngredients } = useSelector(
    (state: any) => state.Ingredients.ingredients
  );
  const [ openModalError, setOpenModalError ] = useState<boolean>(false);
  const [ errorMessage, setErrorMessage ] = useState<string>('');

  useEffect(() => {
    return () => {
      dispatch(resetRequestNewIngradient())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRequestNewIngradient])

  useEffect(() => {
    if (data.id) {
      onRequestSuccess?.(data)
    }
  }, [data, onRequestSuccess])

  useEffect(() => {
    if (error) {
      setOpenModalError(true);
      if (error?.code === 400) {
        setErrorMessage('not_ingredient')
      }
      if (error?.code === 500) {
        setErrorMessage('ai_failed')
      }
      dispatch(resetRequestNewIngradient())
    }
  }, [error, onRequestSuccess])

  const handleSubmit = (name: string) => {
    if (name) {
      const filterName = removeVietnameseTones(name)?.toLowerCase()
      const ingredientFiltered = ingredients.filter((item: TIngredient) => {
        const name = removeVietnameseTones(item.name).toLowerCase()
        return name === filterName
      })
      if (ingredientFiltered.length) {
        onRequestSuccess?.(ingredientFiltered[0])
        return;
      }
      const globalIngredientFiltered = globalIngredients.filter((item: TIngredient) => {
        const name = removeVietnameseTones(item.name).toLowerCase()
        return name === filterName
      })
      if (globalIngredientFiltered.length) {
        onRequestSuccess?.(globalIngredientFiltered[0])
        return;
      }
      dispatch(requestNewIngradient({ingredient_name: name}))
    }
  }

  return {
    handleSubmit,
    loading,
    errorMessage,
    openModalError,
    setOpenModalError,
  }
}
